"use client";

import Link from "next/link";
import { useState } from "react";
import type { Locale } from "@/lib/i18n";
import { toLocalizedPath } from "@/lib/i18n";
import { CONCERN_MAX_LENGTH, CONCERN_MIN_LENGTH, type AiConcernMatch } from "@/lib/ai-concern-match";
import Icon from "./Icon";
type Props = {
  locale: Locale;
};
type ApiResponse = {
  result?: AiConcernMatch;
  error?: string;
};
export default function AiConcernMatchForm({
  locale
}: Props) {
  const [concern, setConcern] = useState("");
  const [result, setResult] = useState<AiConcernMatch | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = locale === "ko" ? {
    label: "요즘 고민",
    placeholder: "예: 친구와 연락 텀이 달라서 서운한데, 내가 예민한 건지 궁금해요.",
    button: "성향 매칭하기",
    loading: "매칭 중...",
    length: `${CONCERN_MIN_LENGTH}-${CONCERN_MAX_LENGTH}자`,
    error: "고민 매칭을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
    invalid: "고민을 10자 이상 입력해 주세요.",
    tags: "성향 키워드",
    recommended: "추천 테스트"
  } : {
    label: "Current concern",
    placeholder: "Example: I feel uneasy because my friend replies much later than I do.",
    button: "Match my tendency",
    loading: "Matching...",
    length: `${CONCERN_MIN_LENGTH}-${CONCERN_MAX_LENGTH} chars`,
    error: "We could not load the match. Please try again shortly.",
    invalid: "Please enter at least 10 characters.",
    tags: "Tendency tags",
    recommended: "Recommended quizzes"
  };
  async function submit() {
    const trimmed = concern.trim();
    if (trimmed.length < CONCERN_MIN_LENGTH) {
      setError(copy.invalid);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const response = await fetch("/api/ai/concern-match", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          concern: trimmed,
          locale
        })
      });
      const data = (await response.json()) as ApiResponse;
      if (!response.ok || !data.result) {
        throw new Error(data.error ?? "request failed");
      }
      setResult(data.result);
    } catch {
      setError(copy.error);
    } finally {
      setPending(false);
    }
  }
  return <section className="card flex flex-col gap-5">
      <label className="flex flex-col gap-2">
        <span className="font-display text-sm text-slate-800">
          {copy.label}
        </span>
        <textarea value={concern} onChange={event => setConcern(event.target.value)} placeholder={copy.placeholder} maxLength={CONCERN_MAX_LENGTH} rows={6} className="min-h-40 resize-y rounded-2xl border border-brand-100 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
      </label>
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>{copy.length}</span>
        <span>{concern.trim().length.toLocaleString()}</span>
      </div>
      <button type="button" onClick={submit} disabled={pending} className="btn-primary inline-flex min-h-11 items-center justify-center gap-2 disabled:opacity-60">
        <Icon name="sparkles" size={16} />
        <span>{pending ? copy.loading : copy.button}</span>
      </button>

      {error ? <p role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </p> : null}

      {result ? <div className="flex flex-col gap-4 border-t border-brand-100 pt-5">
          <div>
            <h2 className="font-display text-lg text-slate-900">
              {result.title}
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              {result.summary}
            </p>
          </div>

          <div>
            <h3 className="font-display text-sm text-slate-800">
              {copy.tags}
            </h3>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {result.tendencyTags.map(tag => <span key={tag} className="chip">
                  #{tag}
                </span>)}
            </div>
          </div>

          <div>
            <h3 className="font-display text-sm text-slate-800">
              {copy.recommended}
            </h3>
            <div className="mt-3 grid gap-3">
              {result.recommendedTests.map(test => <Link key={test.id} href={toLocalizedPath(`/tests/${test.id}`, locale)} className="rounded-2xl bg-white/70 p-4 ring-1 ring-brand-100 transition hover:ring-brand-200">
                  <p className="font-display text-sm text-slate-900">
                    {test.title}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {test.reason}
                  </p>
                </Link>)}
            </div>
          </div>

          <p className="text-xs leading-5 text-slate-400">
            {result.disclaimer}
          </p>
        </div> : null}
    </section>;
}